import { Controller } from "@hotwired/stimulus"

// Mapowanie kluczy z odpowiedzi GUS na selektory pól formularza
const FIELD_MAP = {
  name: "#client_name",
  first_name: "#client_first_name",
  last_name: "#client_last_name",
  shortcut: "#client_shortcut",
  company: "#client_company",
  tax_no: "#client_tax_no",
  register_number: "#client_register_number",
  street: "#client_street",
  post_code: "#client_post_code",
  city: "#client_city",
  province: "#client_province",
  district: "#client_district",
  country: "#client_country",
  email: "#client_email",
  www: "#client_www",
  phone: "#client_phone",
  fax: "#client_fax",
}

// Pola checkbox - wartości "1"/"0" wymagają czytelnych etykiet w oknie konfliktów
const CHECKBOX_FIELDS = new Set(["company"])

export default class extends Controller {
  static values = { url: String, i18n: Object }
  static targets = ["modal", "step1", "step2", "numberInput", "loading", "errorMsg", "conflictsBody"]

  open() {
    this.modalTarget.classList.remove("hidden")
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
    this._clearError()
    this.loadingTarget.classList.add("hidden")
    this.numberInputTarget.value = ""
    this.numberInputTarget.focus()
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  async search(event) {
    event.preventDefault()

    const number = this.numberInputTarget.value.trim()
    if (!number) {
      this._showError(this.i18nValue.empty_input)
      return
    }

    this._clearError()
    this.loadingTarget.classList.remove("hidden")

    const params = this._detectParam(number)
    const csrfToken = document.querySelector("meta[name=\"csrf-token\"]")?.content

    try {
      const response = await window.fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify(params),
      })

      const data = await response.json()

      if (data.errors && data.errors.length > 0) {
        this._showError(data.errors.join(", "))
        return
      }

      this._processResults(data.results || {})
    } catch (_e) {
      this._showError(this.i18nValue.connection_error)
    } finally {
      this.loadingTarget.classList.add("hidden")
    }
  }

  apply() {
    const rows = this.conflictsBodyTarget.querySelectorAll("tr[data-field]")
    rows.forEach(row => {
      const field = row.dataset.field
      const radio = row.querySelector("input[type=\"radio\"]:checked")
      if (!radio) return

      this._setFieldValue(field, radio.dataset.value)
    })
    this.close()
  }

  // --- prywatne ---

  _detectParam(number) {
    const cleaned = number.replace(/[-\s]/g, "")
    // REGON: 9 lub 14 cyfr
    if (/^\d{9}$/.test(cleaned) || /^\d{14}$/.test(cleaned)) return { regon: cleaned }
    // 10 cyfr: NIP lub KRS - rozróżniamy sumą kontrolną NIP
    if (/^\d{10}$/.test(cleaned)) {
      return this._validNip(cleaned) ? { nip: cleaned } : { krs: cleaned }
    }
    // fallback - GUS sam zwaliduje
    return { nip: cleaned }
  }

  _validNip(n) {
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    const sum = weights.reduce((acc, w, i) => acc + w * parseInt(n[i]), 0)
    return sum % 11 === parseInt(n[9])
  }

  _processResults(results) {
    const rows = []

    Object.entries(FIELD_MAP).forEach(([key, selector]) => {
      const field = document.querySelector(selector)
      if (!field) return

      const gusValue = results[key] || ""
      const currentValue = this._getFieldValue(field)

      // te same wartości - nie ma o czym decydować
      if (currentValue.toLowerCase() === gusValue.toLowerCase()) return

      rows.push({
        key,
        label: this._fieldLabel(selector),
        current: currentValue,
        gus: gusValue,
        currentDisplay: currentValue ? this._formatDisplay(key, currentValue) : "-",
        gusDisplay: gusValue ? this._formatDisplay(key, gusValue) : "-",
      })
    })

    if (rows.length === 0) {
      this.close()
      return
    }

    this._showReview(rows)
  }

  _showReview(rows) {
    this.conflictsBodyTarget.innerHTML = ""

    rows.forEach(({ key, label, current, gus, currentDisplay, gusDisplay }) => {
      const row = document.createElement("tr")
      row.dataset.field = key
      row.className = "border-t border-gray-100"

      row.appendChild(this._conflictCell(label, "py-2 pr-3 font-medium text-gray-700"))
      // GUS ma wartość → pre-select GUS; GUS puste → pre-select current (bezpieczne domyślnie)
      const preSelectGus = !!gus
      row.appendChild(this._radioCell(key, current, !preSelectGus, "px-4", currentDisplay, "current"))
      row.appendChild(this._radioCell(key, gus, preSelectGus, "pr-2", gusDisplay, "gus"))

      this.conflictsBodyTarget.appendChild(row)
    })

    this.step1Target.classList.add("hidden")
    this.step2Target.classList.remove("hidden")
  }

  _conflictCell(text, className) {
    const td = document.createElement("td")
    td.className = className
    td.textContent = text
    return td
  }

  selectAllGus() {
    this.conflictsBodyTarget.querySelectorAll("input[data-choice='gus']").forEach(r => { r.checked = true })
  }

  selectAllCurrent() {
    this.conflictsBodyTarget.querySelectorAll("input[data-choice='current']").forEach(r => { r.checked = true })
  }

  _radioCell(key, value, checked, tdClassName, display = null, choiceLabel = null) {
    const td = document.createElement("td")
    td.className = "py-2 " + tdClassName

    const label = document.createElement("label")
    label.className = "flex items-center gap-1 cursor-pointer"

    const input = document.createElement("input")
    input.type = "radio"
    input.name = "gus_conflict_" + key
    input.dataset.value = value
    input.dataset.choice = choiceLabel !== null ? choiceLabel : (checked ? "current" : "gus")
    input.checked = checked
    input.className = "h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mr-1"

    const span = document.createElement("span")
    span.className = "text-gray-700 text-sm"
    span.textContent = display !== null ? display : value

    label.appendChild(input)
    label.appendChild(span)
    td.appendChild(label)
    return td
  }

  _fieldLabel(fieldSelector) {
    const fieldId = fieldSelector.replace("#", "")
    const label = document.querySelector(`label[for="${fieldId}"]`)
    return label ? label.textContent.trim() : fieldId
  }

  _getFieldValue(field) {
    if (field.type === "checkbox") return field.checked ? "1" : "0"
    if (field.tagName === "SELECT") return field.options[field.selectedIndex]?.value || ""
    return field.value || ""
  }

  _setFieldValue(key, value) {
    const selector = FIELD_MAP[key]
    if (!selector) return

    const field = document.querySelector(selector)
    if (!field) return

    if (field.type === "checkbox") {
      field.checked = value === "1"
    } else if (field.tagName === "SELECT") {
      const lower = value.toLowerCase()
      for (let i = 0; i < field.options.length; i++) {
        if (
          field.options[i].value.toLowerCase() === lower ||
          field.options[i].text.toLowerCase() === lower
        ) {
          field.selectedIndex = i
          break
        }
      }
    } else {
      field.value = value
    }

    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _formatDisplay(key, value) {
    if (CHECKBOX_FIELDS.has(key)) {
      return value === "1" ? this.i18nValue.bool_yes : this.i18nValue.bool_no
    }
    return value
  }

  _showError(msg) {
    this.errorMsgTarget.textContent = msg
    this.errorMsgTarget.classList.remove("hidden")
  }

  _clearError() {
    this.errorMsgTarget.textContent = ""
    this.errorMsgTarget.classList.add("hidden")
  }
}
